// @ts-nocheck
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import RegistrationPricingTab from "./event-pricing-tabs/RegistrationPricingTab";
import ExtrasTab from "./event-pricing-tabs/ExtrasTab";
import SpectatorTicketsTab from "./event-pricing-tabs/SpectatorTicketsTab";
import DiscountsTab from "./event-pricing-tabs/DiscountsTab";
import PaymentMethodsTab from "./event-pricing-tabs/PaymentMethodsTab";

interface PaymentMethod {
  type: 'stripe' | 'pay_later' | 'venmo';
  enabled: boolean;
  label: string;
  instructions?: string;
  venmo_handle?: string;
}

interface PricingStructure {
  registration_tiers?: Array<{
    name: string;
    price: number;
    description?: string;
    form_fields?: string[];
  }>;
  division_pricing?: Array<{
    name: string;
    price: number;
    form_fields?: string[];
  }>;
  extras?: Array<{
    name: string;
    price: number;
    description?: string;
    required: boolean;
    max_qty?: number;
    image_url?: string;
  }>;
  spectator_tickets?: Array<{
    name: string;
    price: number;
    description?: string;
    quantity_available?: number;
    is_active: boolean;
  }>;
  discount_codes?: Array<{
    code: string;
    type: "percentage" | "fixed" | "per_team_volume";
    value: number;
    valid_from?: string;
    valid_until?: string;
    usage_limit?: number;
    is_active: boolean;
  }>;
  multi_team_discount?: {
    enabled: boolean;
    min_teams: number;
    discount_per_team: number;
    description: string;
  };
  circuit_codes?: Array<{
    code: string;
    discount_per_team: number;
    description: string;
    overrides_multi_team: boolean;
    is_active: boolean;
  }>;
  processing_fee?: {
    enabled: boolean;
    percentage: number;
    applies_to: string;
    description: string;
  };
  payment_methods?: PaymentMethod[];
  roster_config?: {
    min_players?: number;
    max_players?: number;
    deadline?: string;
  };
}

interface EventPricingData {
  id?: string;
  event_id: string;
  organization_id: string;
  pricing_type: "individual" | "team";
  base_price: number;
  currency: string;
  is_active: boolean;
  pricing_structure: PricingStructure;
}

export default function EventPricingConfig() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { organization } = useOrganizationContext();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventName, setEventName] = useState("");
  const [pricingData, setPricingData] = useState<EventPricingData>({
    event_id: eventId || "",
    organization_id: organization?.id || "",
    pricing_type: "individual",
    base_price: 0,
    currency: "USD",
    is_active: true,
    pricing_structure: {
      registration_tiers: [],
      division_pricing: [],
      extras: [],
      spectator_tickets: [],
      discount_codes: [],
      payment_methods: [
        { type: 'stripe', enabled: true, label: 'Credit Card (Stripe)' },
        { type: 'pay_later', enabled: false, label: 'Pay Later', instructions: '' },
        { type: 'venmo', enabled: false, label: 'Venmo', instructions: '', venmo_handle: '' },
      ],
      roster_config: {},
    },
  });

  useEffect(() => {
    if (eventId && organization?.id) {
      loadEventPricing();
      loadEventName();
    }
  }, [eventId, organization?.id]);

  const loadEventName = async () => {
    if (!eventId) return;
    
    const { data, error } = await supabase
      .from("events")
      .select("name")
      .eq("id", eventId)
      .single();

    if (!error && data) {
      setEventName(data.name);
    }
  };

  const loadEventPricing = async () => {
    if (!eventId || !organization?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("event_pricing")
        .select("*")
        .eq("event_id", eventId)
        .eq("organization_id", organization.id)
        .single();

      if (!error && data) {
        // Extract metadata from pricing_structure JSONB
        const structure = data.pricing_structure as PricingStructure & {
          pricing_type?: "individual" | "team";
          base_price?: number;
          currency?: string;
          is_active?: boolean;
        };
        
        setPricingData({
          id: data.id,
          event_id: data.event_id,
          organization_id: data.organization_id,
          pricing_type: structure.pricing_type || "individual",
          base_price: structure.base_price || 0,
          currency: structure.currency || "USD",
          is_active: structure.is_active !== false,
          pricing_structure: {
            registration_tiers: structure.registration_tiers || [],
            division_pricing: structure.division_pricing || [],
            extras: structure.extras || [],
            spectator_tickets: structure.spectator_tickets || [],
            discount_codes: structure.discount_codes || [],
            multi_team_discount: structure.multi_team_discount,
            circuit_codes: structure.circuit_codes || [],
            processing_fee: structure.processing_fee,
            payment_methods: structure.payment_methods || [
              { type: 'stripe', enabled: true, label: 'Credit Card (Stripe)' },
              { type: 'pay_later', enabled: false, label: 'Pay Later', instructions: '' },
              { type: 'venmo', enabled: false, label: 'Venmo', instructions: '', venmo_handle: '' },
            ],
            roster_config: structure.roster_config || {},
          },
        });
      }
    } catch (err) {
      console.error("Error loading pricing:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateBasePrice = (structure: PricingStructure): number => {
    if (structure.registration_tiers && structure.registration_tiers.length > 0) {
      return Math.min(...structure.registration_tiers.map(t => t.price));
    }
    if (structure.division_pricing && structure.division_pricing.length > 0) {
      return Math.min(...structure.division_pricing.map(d => d.price));
    }
    return 0;
  };

  const handleSave = async () => {
    if (!eventId || !organization?.id) {
      toast({
        title: "Error",
        description: "Missing event or organization information",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const basePrice = calculateBasePrice(pricingData.pricing_structure);
      
      // Store all pricing config inside pricing_structure JSONB
      const dataToSave = {
        event_id: eventId,
        organization_id: organization.id,
        pricing_structure: {
          ...pricingData.pricing_structure,
          pricing_type: pricingData.pricing_type,
          base_price: basePrice,
          currency: "USD",
          is_active: true,
        },
      };

      const { error } = pricingData.id
        ? await supabase
            .from("event_pricing")
            .update(dataToSave)
            .eq("id", pricingData.id)
        : await supabase.from("event_pricing").insert([dataToSave]);

      if (error) throw error;

      // Sync to old tables for backward compatibility with old registration system
      // Sync extras to event_extras table
      if (pricingData.pricing_structure.extras && pricingData.pricing_structure.extras.length > 0) {
        // Delete existing extras for this event
        await supabase.from("event_extras").delete().eq("event_id", eventId);
        
        // Insert new extras
        const extrasToInsert = pricingData.pricing_structure.extras.map(extra => ({
          event_id: eventId,
          name: extra.name,
          description: extra.description || null,
          price: extra.price,
          is_required: extra.required || false,
          max_quantity: extra.max_qty || null,
          thumbnail_url: extra.image_url || null,
          is_active: true,
        }));
        
        const { error: extrasError } = await supabase.from("event_extras").insert(extrasToInsert);
        if (extrasError) console.error("Error syncing extras:", extrasError);
      }

      // Sync spectator tickets to event_spectator_tickets table
      if (pricingData.pricing_structure.spectator_tickets && pricingData.pricing_structure.spectator_tickets.length > 0) {
        // Delete existing tickets for this event
        await supabase.from("event_spectator_tickets").delete().eq("event_id", eventId);
        
        // Insert new tickets
        const ticketsToInsert = pricingData.pricing_structure.spectator_tickets.map(ticket => ({
          event_id: eventId,
          name: ticket.name,
          price: ticket.price,
          description: ticket.description || null,
          quantity_available: ticket.quantity_available || null,
          is_active: ticket.is_active !== false,
        }));
        
        const { error: ticketsError } = await supabase.from("event_spectator_tickets").insert(ticketsToInsert);
        if (ticketsError) console.error("Error syncing tickets:", ticketsError);
      }

      // Sync roster_deadline to events table for backward compatibility
      const rosterDeadline = pricingData.pricing_structure.roster_config?.deadline || null;
      const { error: eventsUpdateError } = await supabase
        .from("events")
        .update({ roster_deadline: rosterDeadline })
        .eq("id", eventId);
      
      if (eventsUpdateError) {
        console.error("Error syncing roster_deadline to events:", eventsUpdateError);
      }

      toast({
        title: "Success",
        description: "Event pricing configuration saved successfully",
      });
    } catch (error) {
      console.error("Error saving pricing:", error);
      toast({
        title: "Error",
        description: "Failed to save pricing configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updatePricingStructure = (updates: Partial<PricingStructure>) => {
    setPricingData(prev => ({
      ...prev,
      pricing_structure: {
        ...prev.pricing_structure,
        ...updates,
      },
    }));
  };

  if (loading) {
    return (
      <div className="container max-w-6xl mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-muted-foreground">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Event Pricing Configuration</h1>
            <p className="text-sm text-muted-foreground">{eventName}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      {/* Tabbed Interface */}
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="registration" className="w-full">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 mb-6">
              <TabsTrigger value="registration">Registration</TabsTrigger>
              <TabsTrigger value="extras">Extras</TabsTrigger>
              <TabsTrigger value="spectator">Spectator</TabsTrigger>
              <TabsTrigger value="discounts">Discounts</TabsTrigger>
              <TabsTrigger value="payment">Payment</TabsTrigger>
            </TabsList>

            <TabsContent value="registration">
              <RegistrationPricingTab
                eventId={eventId}
                pricingType={pricingData.pricing_type}
                setPricingType={(type) => setPricingData(prev => ({ ...prev, pricing_type: type }))}
                registrationTiers={pricingData.pricing_structure.registration_tiers || []}
                divisionPricing={pricingData.pricing_structure.division_pricing || []}
                rosterConfig={pricingData.pricing_structure.roster_config || {}}
                onUpdate={updatePricingStructure}
              />
            </TabsContent>

            <TabsContent value="extras">
              <ExtrasTab
                extras={pricingData.pricing_structure.extras || []}
                onUpdate={(extras) => updatePricingStructure({ extras })}
              />
            </TabsContent>

            <TabsContent value="spectator">
              <SpectatorTicketsTab
                tickets={pricingData.pricing_structure.spectator_tickets || []}
                onUpdate={(spectator_tickets) => updatePricingStructure({ spectator_tickets })}
              />
            </TabsContent>

            <TabsContent value="discounts">
              <DiscountsTab
                discountCodes={pricingData.pricing_structure.discount_codes || []}
                multiTeamDiscount={pricingData.pricing_structure.multi_team_discount}
                circuitCodes={pricingData.pricing_structure.circuit_codes || []}
                processingFee={pricingData.pricing_structure.processing_fee}
                onUpdate={(data) => updatePricingStructure({
                  discount_codes: data.discountCodes,
                  multi_team_discount: data.multiTeamDiscount,
                  circuit_codes: data.circuitCodes,
                  processing_fee: data.processingFee
                })}
              />
            </TabsContent>

            <TabsContent value="payment">
              <PaymentMethodsTab
                paymentMethods={pricingData.pricing_structure.payment_methods || []}
                onUpdate={(payment_methods) => updatePricingStructure({ payment_methods })}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
